import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { ErrorAlert } from "../common/snackbar/error";
import { SuccessAlert } from "../common/snackbar/success";
import SpinnerImg from "../../images/loading.svg";
import { makeStyles } from "@mui/styles";
import secureLocalStorage from "react-secure-storage";

const useStyles = makeStyles((theme) => ({
  spinner: {
    animation: "spin 2s linear infinite",
  },
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
}));

const baseUrl = process.env.REACT_APP_BASE_URL;

// Browser-compatible API request function
const makeApiRequest = async (url, payload, headers = {}, returnText = false) => {
  const token = secureLocalStorage.getItem("token");
  const finalHeaders = {
    "Content-Type": "application/json",
    ...headers,
  };

  if (token) {
    finalHeaders["X-Authorization"] = token;
  }

  const response = await fetch(`${baseUrl}${url}`, {
    method: "POST",
    headers: finalHeaders,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  // For createmicroformtoken, return plain text JWT token
  if (returnText) {
    return response.text();
  }

  return response.json();
};

function SpinnerShow() {
  const classes = useStyles();
  return (
    <Grid
      item
      width={"100%"}
      justifyContent={"center"}
      display={"flex"}
      alignItems={"center"}
    >
      <Box
        component={"img"}
        className={classes.spinner}
        alt="spinner"
        src={SpinnerImg}
      />
    </Grid>
  );
}

function OtpPage({ enrolledData, onComplete, setIsloading, isLoading }) {
  const [iframeVisible, setIframeVisible] = useState(true);
  const formRef = useRef(null);

  useEffect(() => {
    console.log("OtpPage enrolledData:", enrolledData);

    const handleMessage = (event) => {
      console.log("Message received:", event.data, "Origin:", event.origin);
      if (event.origin !== baseUrl) {
        console.warn("Unexpected origin:", event.origin);
        return;
      }
      if (event.data?.action === "closeIframe") {
        console.log("Calling onComplete with:", event.data.data);
        setIframeVisible(false);
        setIsloading(true);
        setTimeout(() => {
          onComplete(event.data.data);
          setIsloading(false);
        }, 1000);
      }
    };

    window.addEventListener("message", handleMessage);

    // Submit form after a short delay to ensure it's rendered
    const submitTimeout = setTimeout(() => {
      if (formRef.current) {
        formRef.current.submit();
      }
    }, 100);

    return () => {
      window.removeEventListener("message", handleMessage);
      clearTimeout(submitTimeout);
    };
  }, [enrolledData, onComplete, setIsloading]);
  return (
    <div>
      {isLoading && <SpinnerShow />}
      {iframeVisible && (
        <>
          <iframe
            id="step-up-iframe"
            name="step-up-iframe"
            height="400"
            width="100%"
          ></iframe>
          <form
            ref={formRef}
            id="step-up-form"
            target="step-up-iframe"
            method="post"
            action={enrolledData.consumerAuthenticationInformation.stepUpUrl}
          >
            <input
              type="hidden"
              name="JWT"
              value={enrolledData.consumerAuthenticationInformation.accessToken}
            />
            <input
              type="hidden"
              name="MD"
              value="optionally_include_custom_data_that_will_be_returned_as_is"
            />
          </form>
        </>
      )}
    </div>
  );
}

function SetUpFrame({ setUpdata, onComplete, setIsloading }) {
  const formRef = useRef(null);
  useEffect(() => {
    setIsloading(true);

    const handleCollectionMessage = (event) => {
      if (event.origin === "https://centinelapi.cardinalcommerce.com") {
        onComplete();
        setIsloading(false);
      }
    };

    window.addEventListener("message", handleCollectionMessage, false);

    // Submit form after a short delay to ensure it's rendered
    const submitTimeout = setTimeout(() => {
      if (formRef.current) {
        formRef.current.submit();
      }
    }, 100);

    return () => {
      window.removeEventListener("message", handleCollectionMessage);
      clearTimeout(submitTimeout);
    };
  }, [onComplete, setIsloading]);

  return (
    <div>
      <iframe
        name="collectionIframe"
        id="cardinal_collection_iframe"
        height="400"
        width="400"
        style={{ display: "none" }}
        title="Cardinal Collection"
      ></iframe>
      <form
        ref={formRef}
        id="cardinal_collection_form"
        target="collectionIframe"
        method="post"
        action={
          setUpdata.consumerAuthenticationInformation.deviceDataCollectionUrl
        }
      >
        <input
          type="hidden"
          name="JWT"
          value={setUpdata.consumerAuthenticationInformation.accessToken}
        />
      </form>
    </div>
  );
}

function Microform({
  invoiceBalance,
  type,
  reference,
  businessNumber,
  onPaymentResponse,
  setLoading,
  disableTermlyFee,
}) {
  var microform = useRef(null);
  const mytoKen = useRef(null);
  const [setUpdata, setSetUpData] = useState(null);
  const [enrolledData, setEnrolledData] = useState(null);
  const [cardHolderName, setCardHolderName] = useState("");
  const [isLoading, setIsloading] = useState(false);
  const [expMonth, setExpMonth] = useState("01");
  const [expYear, setExpYear] = useState(2025);
  const [defaultCurrency] = useState("KES");
  const [invoiceNumber] = useState(reference);
  const [amount, setAmount] = useState(invoiceBalance);
  const [errorShow, setErrorShow] = useState({ state: false, message: "" });
  const [successShow, setSuccessShow] = useState({ state: false, message: "" });

  const splited = cardHolderName?.split(" ");
  const firstName = splited?.[0] || "";
  const lastName = splited?.[splited?.length - 1] || "";

  const decodeJWT = (token) => {
    try {
      const parts = token.split(".");
      if (parts.length !== 3) {
        throw new Error("Invalid JWT format");
      }
      const payload = JSON.parse(
        atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
      );
      return payload;
    } catch (error) {
      console.error("Error decoding token:", error?.message);
      return null;
    }
  };

  const payButtonClicked = async () => {
    setIsloading(true);
    const options = {
      expirationMonth: expMonth,
      expirationYear: expYear,
    };
    microform.current.createToken(options, async function (err, token) {
      if (err) {
        console.log(err);
        setErrorShow({ state: true, message: err.message || "Failed to create token" });
        setIsloading(false);
      } else {
        console.log(JSON.stringify(token), "microform token generated ✅");
        setIsloading(false);
        mytoKen.current = token;
        setUp3D();
      }
    });
  };

  const setUp3D = async () => {
    setIsloading(true);
    try {
      let cybsResponse = await makeApiRequest(
        `/api/v1/payments/setup3dauth`,
        {
          token: mytoKen.current,
          reference: invoiceNumber,
          businessNumber: businessNumber,
        }
      );
      setSetUpData(cybsResponse);
      setIsloading(false);
      console.log(
        cybsResponse,
        "Authentication setup response for 3D payment"
      );
    } catch (error) {
      console.log(error?.message, "Error setting up 3D payments ❌❌");
      setErrorShow({ state: true, message: error?.message || "Failed to setup 3D authentication" });
      setIsloading(false);
    }
  };

  const checkEnrollment = async () => {
    setIsloading(true);
    if (setLoading) setLoading(true);
    try {
      let cybsResponse = await makeApiRequest(
        `/api/v1/payments/checkenrollment`,
        {
          token: mytoKen.current,
          currency: defaultCurrency ?? "KES",
          amount: amount,
          businessNumber: businessNumber,
          referenceId: setUpdata.consumerAuthenticationInformation.referenceId,
          reference: invoiceNumber,
        }
      );
      console.log(cybsResponse, "check enrollment response ### 🚀🚀🚀");

      // Check the status of the response
      const status = cybsResponse?.status;

      // If authentication failed, show error
      if (status === "AUTHENTICATION_FAILED") {
        setErrorShow({
          state: true,
          message: cybsResponse?.errorInformation?.message || "Authentication failed. Please try again."
        });
        setIsloading(false);
        if (setLoading) setLoading(false);
        return;
      }

      // If pending authentication (3D Secure challenge required), proceed to OTP page
      if (status === "PENDING_AUTHENTICATION") {
        // Verify stepUpUrl exists before setting enrolled data
        if (!cybsResponse?.consumerAuthenticationInformation?.stepUpUrl) {
          setErrorShow({
            state: true,
            message: "Invalid authentication response. Please try again."
          });
          setIsloading(false);
          if (setLoading) setLoading(false);
          return;
        }

        setEnrolledData(cybsResponse);
        setIsloading(false);
        if (setLoading) setLoading(false);
        return;
      }

      // Handle any other unexpected status
      setErrorShow({
        state: true,
        message: cybsResponse?.errorInformation?.message || "Unexpected authentication response. Please try again."
      });
      setIsloading(false);
      if (setLoading) setLoading(false);
    } catch (error) {
      console.log(error);
      setErrorShow({ state: true, message: error?.message || "Failed to check enrollment" });
      setIsloading(false);
      if (setLoading) setLoading(false);
    }
  };

  const sendPayment = async (authdata) => {
    console.log(JSON.stringify(authdata), "received authdata");
    setIsloading(true);
    if (setLoading) setLoading(true);
    try {
      let cybsResponse = await makeApiRequest(
        `/api/v1/payments/validatepaymentresult`,
        {
          jwt: mytoKen.current,
          currency: "KES",
          amount: amount,
          businessNumber: businessNumber,
          reference: invoiceNumber,
          cavv: authdata.consumerAuthenticationInformation.cavv,
          xid: authdata.consumerAuthenticationInformation.xid,
          acsTransactionId:
            authdata?.consumerAuthenticationInformation?.acsTransactionId,
          eci: authdata?.consumerAuthenticationInformation?.eci,
          directoryServerTransactionId:
            authdata?.consumerAuthenticationInformation
              ?.directoryServerTransactionId,
          specificationVersion:
            authdata?.consumerAuthenticationInformation?.specificationVersion,
          paresStatus: authdata?.consumerAuthenticationInformation?.paresStatus,
          tokenauth: authdata?.consumerAuthenticationInformation?.token,
          threeDSServerTransactionId:
            authdata?.consumerAuthenticationInformation
              ?.threeDSServerTransactionId,
          expirationMonth: expMonth,
          TransactionId:
            enrolledData?.consumerAuthenticationInformation
              ?.authenticationTransactionId,
          expirationYear: expYear,
          paymentChanel: "Web",
          type: type,
          firstName: firstName,
          lastName: lastName,
          cardHolderName: cardHolderName,
        }
      );
      console.log(
        cybsResponse,
        "validating authentication results ⚡️⚡️⚡️⚡️"
      );
      if (
        cybsResponse?.status === "Success" ||
        cybsResponse?.Status === "SUCCESS"
      ) {
        setIsloading(false);
        if (setLoading) setLoading(false);

        if (onPaymentResponse) {
          onPaymentResponse("SUCCESS");
        } else {
          setSuccessShow({ state: true, message: "Payment was successful" });
        }
      } else {
        setIsloading(false);
        if (setLoading) setLoading(false);

        if (onPaymentResponse) {
          onPaymentResponse("FAILED");
        } else {
          setErrorShow({ state: true, message: "Payment was not successful" });
        }
      }
    } catch (error) {
      console.log(error);
      setIsloading(false);
      if (setLoading) setLoading(false);

      if (onPaymentResponse) {
        onPaymentResponse("FAILED");
      } else {
        setErrorShow({ state: true, message: error?.message || "Payment failed" });
      }
    }
  };

  useEffect(() => {
    const createForm = async () => {
      let token = null;
      let integrity = null;
      let libraryUrl = null;
      try {
        // createmicroformtoken endpoint returns plain text JWT, not JSON
        const tokenResponse = await makeApiRequest(
          `/api/v1/payments/createmicroformtoken`,
          {
            businessNumber: businessNumber,
            invoiceNumber: invoiceNumber || reference,
          },
          {},
          true // returnText = true for JWT token response
        );
        token = tokenResponse;
        let clientLibrary = decodeJWT(token).ctx[0]?.data;
        libraryUrl = clientLibrary.clientLibrary;
        integrity = clientLibrary.clientLibraryIntegrity;
      } catch (e) {
        console.log(e?.message);
        setErrorShow({ state: true, message: e?.message || "Failed to initialize payment form" });
      }

      if (libraryUrl) {
        const head = window.document.getElementsByTagName("head")[0];
        const script = window.document.createElement("script");
        script.type = "text/javascript";
        script.async = true;
        script.onload = function () {
          const mystyles = {};
          const flex = new window.Flex(token);
          microform.current = flex.microform({ styles: mystyles });
          const number = microform.current.createField("number", {
            placeholder: "Enter card number",
          });
          const securityCode = microform.current.createField("securityCode", {
            placeholder: "123",
          });
          number.load("#number-container");
          securityCode.load("#securityCode-container");
        };
        script.src = libraryUrl;
        if (integrity) {
          script.integrity = integrity;
          script.crossOrigin = "anonymous";
        }
        head.appendChild(script);
      }
    };
    createForm();
  }, [businessNumber, reference, invoiceNumber]);

  if (enrolledData) {
    return (
      <OtpPage
        enrolledData={enrolledData}
        setIsloading={setIsloading}
        isLoading={isLoading}
        onComplete={(e) => sendPayment(e)}
      />
    );
  } else {
    return (
      <Box component={"div"} display={"flex"} flexDirection={"column"} px={1}>
        <ErrorAlert
          vertical="top"
          horizontal="right"
          onClose={() => setErrorShow({ ...errorShow, state: false })}
          open={errorShow.state}
          message={errorShow.message}
        />
        <SuccessAlert
          vertical="top"
          horizontal="right"
          onClose={() => setSuccessShow({ ...successShow, state: false })}
          open={successShow.state}
          message={successShow.message}
        />
        <Box display="flex" alignItems="center" gap={2} py={1} width={"100%"}>
          <Box component={"div"} flex={1}>
            <InputLabel
              htmlFor="cardholderName"
              sx={{ color: "#032541", fontWeight: 600 }}
            >
              Cardholder Name
            </InputLabel>
            <TextField
              value={cardHolderName}
              onChange={(e) => setCardHolderName(e.target.value)}
              id="cardholderName"
              name="cardholderName"
              placeholder="John Doe"
              fullWidth
              variant="outlined"
            />
          </Box>
          <Box component={"div"} flex={1}>
            <InputLabel
              htmlFor="totalAmount"
              sx={{ color: "#032541", fontWeight: 600 }}
            >
              Amount
            </InputLabel>
            <TextField
              value={amount}
              disabled={disableTermlyFee}
              onChange={(e) => setAmount(e.target.value)}
              id="totalAmount"
              name="totalAmount"
              placeholder="100"
              fullWidth
              variant="outlined"
            />
          </Box>
        </Box>
        <Box
          component={"div"}
          display="flex"
          alignItems="center"
          gap={2}
          mt={2}
          mb={2}
        >
          <Box flex={1}>
            <Typography fontWeight={600} fontSize={16} color="#032541">
              Card Number
            </Typography>
            <Box
              component={"div"}
              border="1px solid #ccc"
              p={1}
              mt={1}
              borderRadius={1}
              height={55}
              id="number-container"
            />
          </Box>
          <Box flex={1}>
            <Typography fontWeight={600} fontSize={16} color="#032541">
              Security Code
            </Typography>
            <Box
              component={"div"}
              border="1px solid #ccc"
              p={1}
              mt={1}
              borderRadius={1}
              height={55}
              id="securityCode-container"
            />
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={2} mt={3}>
          <FormControl fullWidth>
            <InputLabel
              sx={{ color: "#032541", fontWeight: 600 }}
              htmlFor="expMonth"
            >
              Expiry Month
            </InputLabel>
            <Select
              id="expMonth"
              label="Expiry Month"
              name="expMonth"
              value={expMonth}
              onChange={(e) => setExpMonth(e.target.value)}
            >
              {[...Array(12).keys()].map((m) => {
                const padded = String(m + 1).padStart(2, "0");
                return (
                  <MenuItem key={padded} value={padded}>
                    {padded}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel
              sx={{ color: "#032541", fontWeight: 600 }}
              htmlFor="expYear"
            >
              Expiry Year
            </InputLabel>
            <Select
              onChange={(e) => setExpYear(e.target.value)}
              id="expYear"
              label="Expiry Year"
              name="expYear"
              defaultValue={2025}
            >
              {[...Array(10).keys()].map((y) => (
                <MenuItem key={2025 + y} value={2025 + y}>
                  {2025 + y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button
            onClick={() => payButtonClicked()}
            id="pay-button"
            variant="contained"
            style={
              isLoading ||
              !firstName ||
              !lastName ||
              !expMonth ||
              !expYear ||
              !reference ||
              !invoiceNumber ||
              invoiceNumber === "undefined" ||
              invoiceNumber === undefined ||
              !amount ||
              amount <= 0
                ? {
                    backgroundColor: "#b2b4bb",
                    color: "#fff",
                    mt: 2,
                    textTransform: "inherit",
                    width: "140px",
                  }
                : {
                    background: "#032541",
                    color: "#fff",
                    mt: 2,
                    textTransform: "inherit",
                    width: "140px",
                  }
            }
            disabled={
              isLoading ||
              !firstName ||
              !lastName ||
              !expMonth ||
              !expYear ||
              !amount ||
              amount <= 0
            }
          >
            {isLoading ? "Processing..." : "Pay Now"}
          </Button>
        </Box>
        {isLoading && <SpinnerShow />}
        {setUpdata && (
          <SetUpFrame
            isLoading={isLoading}
            setUpdata={setUpdata}
            setIsloading={setIsloading}
            onComplete={() => {
              checkEnrollment();
            }}
          />
        )}
      </Box>
    );
  }
}

function PaymentForm({
  invoiceBalance,
  type,
  reference,
  businessNumber,
  onPaymentResponse,
  setLoading,
  disableTermlyFee,
}) {
  return (
    <Box component={"div"} width={"100%"}>
      <Microform
        invoiceBalance={invoiceBalance}
        type={type}
        reference={reference}
        businessNumber={businessNumber}
        onPaymentResponse={onPaymentResponse}
        setLoading={setLoading}
        disableTermlyFee={disableTermlyFee}
      />
    </Box>
  );
}

export default PaymentForm;
